import log from "loglevel";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { FileIcon } from "@twilio-paste/icons/esm/FileIcon";
import { CloseIcon } from "@twilio-paste/icons/esm/CloseIcon";
import { Button } from "@twilio-paste/core/button";
import { Media } from "@twilio/conversations";
import { extension as mimeToExtension } from "mime-types";
import { Truncate } from "@twilio-paste/core/truncate";

import { addNotification, detachFiles } from "../store/actions/genericActions";
import { AppState } from "../store/definitions";
import { notifications } from "../notifications";
import { roundFileSizeInMB } from "../utils/roundFileSizeInMB";
import { reportClientError } from "../utils/reportClientError";
import { extrairCodigos } from "../utils/diagnosticoDeEnvio";
import { descreverErro } from "../utils/descreverErro";
import {
    actionIconContainerStyles,
    fileDescriptionContainerStyles,
    fileIconContainerStyles,
    fileNameStyles,
    fileSizeStyles,
    getContainerStyles,
    outerContainerStyles
} from "./styles/FilePreview.styles";

interface FilePreviewProps {
    file: File;
    isBubble: boolean;
    disabled?: boolean;
    media?: Media;
    focusable: boolean;
}

export const FilePreview = (props: FilePreviewProps) => {
    const { file, isBubble, disabled, media, focusable } = props;
    const [isHovered, setIsHovered] = useState(false);

    const dispatch = useDispatch();
    const fileAttachmentConfig = useSelector((state: AppState) => state.config.fileAttachment);

    const handleDetach = () => {
        dispatch(detachFiles([file]));
    };

    const handleDownload = async () => {
        if (fileAttachmentConfig?.maxFileSize && file.size > fileAttachmentConfig.maxFileSize) {
            dispatch(
                addNotification(
                    notifications.fileDownloadInvalidSizeNotification({
                        fileName: file.name,
                        maxFileSize: `${roundFileSizeInMB(fileAttachmentConfig.maxFileSize)}MB`
                    })
                )
            );
            return;
        }

        if (
            fileAttachmentConfig?.acceptedExtensions &&
            !fileAttachmentConfig.acceptedExtensions.includes(mimeToExtension(file.type) as string)
        ) {
            dispatch(
                addNotification(
                    notifications.fileDownloadInvalidTypeNotification({
                        fileName: file.name
                    })
                )
            );
            return;
        }

        try {
            const url = media ? await media.getContentTemporaryUrl() : URL.createObjectURL(file);
            if (!url) {
                log.error("Failed downloading message attachment: no content URL returned");
                /*
                 * Auditoria de observabilidade (2026-08-28), gap W4: o `log.error` acima é console
                 * do NAVEGADOR DELE — do nosso lado a falha era invisível. O cliente clica no print
                 * que o atendente mandou e nada abre.
                 *
                 * 🔒 PII: o nome do arquivo é dado do cliente ("comprovante-joao.pdf"). Só o motivo
                 * técnico entra aqui — nunca `file.name`.
                 */
                reportClientError("media_open_failed", { reason: "anexo sem URL de conteúdo" });
                return;
            }
            window.open(url);
        } catch (e) {
            /*
             * 🔒 O CONSOLE TAMBÉM É SUPERFÍCIE. O widget é injetado DIRETO no DOM do site da marca,
             * sem iframe: qualquer script daquela página lê o console. Interpolar o erro cru publica
             * ali o `toString()` dele — nome + MENSAGEM (não a stack, que só sai quando se passa o
             * objeto) — e num caminho de ANEXO a mensagem carrega nome de arquivo,
             * que é dado do apostador. Era o último ponto da base que gravava o objeto cru; o resto
             * já usa `descreverErro`.
             */
            log.error(`Failed downloading message attachment: ${descreverErro(e)}`);
            reportClientError("media_open_failed", { reason: descreverErro(e), ...extrairCodigos(e) });
        }
    };

    const handleBoxClick = () => {
        if (!disabled) {
            handleDownload();
        }
    };

    return (
        <Box {...outerContainerStyles}>
            <Box
                as="button"
                type="button"
                appearance="none"
                data-test="file-preview-main-area"
                {...getContainerStyles(isBubble, disabled)}
                onClick={handleBoxClick}
                onMouseEnter={() => setIsHovered(true)}
                onFocus={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onBlur={() => setIsHovered(false)}
                tabIndex={focusable ? 0 : -1}
            >
                <Box {...fileIconContainerStyles}>
                    <FileIcon decorative={false} title="Anexo" size="sizeIcon40" />
                </Box>
                <Box {...fileDescriptionContainerStyles}>
                    <Text as="h1" {...fileNameStyles}>
                        <Truncate title={file.name}>{file.name}</Truncate>
                    </Text>
                    <Text as="p" {...fileSizeStyles}>
                        {isHovered ? "Clique para abrir" : `${roundFileSizeInMB(file.size)}MB`}
                    </Text>
                </Box>
            </Box>
            <Box {...actionIconContainerStyles}>
                {!isBubble && (
                    <Button
                        variant="secondary_icon"
                        size="icon_small"
                        onClick={handleDetach}
                        type="button"
                        data-test="message-file-attachment-remove-button"
                    >
                        <CloseIcon
                            decorative={false}
                            title="Remover anexo"
                            size="sizeIcon30"
                            color="inherit"
                        />
                    </Button>
                )}
            </Box>
        </Box>
    );
};
